import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import ButtonBase from '@mui/material/ButtonBase';
import CardContent from '@mui/material/CardContent';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project-imports
import Avatar from 'components/@extended/Avatar';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';
import MainCard from 'components/MainCard';
import useAuth from 'hooks/useAuth';

// assets
import { Logout } from 'iconsax-reactjs';

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

export default function ProfileMenu() {
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  const initialsFromNames =
    (user?.firstName?.trim()?.charAt(0)?.toUpperCase() ?? '') +
    (user?.lastName?.trim()?.charAt(0)?.toUpperCase() ?? '');
  const userInitial =
    initialsFromNames.length > 0
      ? initialsFromNames
      : (user?.name?.trim()?.charAt(0)?.toUpperCase() ?? 'U');

  const handleLogout = () => {
    try {
      logout();
      navigate('/login', { state: { from: '' } });
    } catch {
      // no-op
    }
  };

  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    const target = event.target;
    if (anchorRef.current && target instanceof Node && anchorRef.current.contains(target)) {
      return;
    }
    setOpen(false);
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <ButtonBase
        sx={(theme) => ({
          p: 0.25,
          borderRadius: 1,
          '&:hover': { bgcolor: 'secondary.lighter', ...theme.applyStyles('dark', { bgcolor: 'secondary.light' }) },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.secondary.dark}`,
            outlineOffset: 2
          }
        })}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-menu' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Avatar alt="User" size="sm">
          {userInitial}
        </Avatar>
      </ButtonBase>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper
              sx={(theme) => ({
                boxShadow: theme.customShadows.z1,
                width: 280,
                minWidth: 240,
                borderRadius: 1.5
              })}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard border={false} content={false}>
                  <CardContent sx={{ px: 2.5, pt: 3 }}>
                    <Grid container sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <Grid size={10}>
                        <Stack direction="row" sx={{ gap: 1.25, alignItems: 'center' }}>
                          <Avatar alt="User" size="sm">
                            {userInitial}
                          </Avatar>
                          <Stack>
                            <Typography variant="subtitle1">
                              {[user?.firstName, user?.lastName].filter(Boolean).join(' ') ?? user?.name ?? 'User'}
                            </Typography>
                            {user?.email && (
                              <Typography variant="body2" color="secondary">
                                {user.email}
                              </Typography>
                            )}
                          </Stack>
                        </Stack>
                      </Grid>
                      <Grid size={2}>
                        <Tooltip title="Logout">
                          <IconButton size="large" color="error" sx={{ p: 1 }} onClick={handleLogout}>
                            <Logout variant="Bulk" />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </CardContent>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
